// src/components/HeroSection/HeroSection.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { motion, AnimatePresence, useAnimationControls } from 'framer-motion';
import { Play, Pause, ChevronLeft, ChevronRight } from 'lucide-react';
import { HEROES } from '../lib/graphql/queries';
import { fileUrl } from '../lib/utils';
import { useParams } from 'react-router-dom';
import { useIsMobile } from '../hooks/use-mobile';

interface HeroSection {
  id: string;
  title: string;
  subtitle?: string;
  ctaText?: string;
  ctaLink?: string;
  type: 'VIDEO' | 'IMAGE';
  mediaUrl: string;
  thumbnailUrl?: string;
  order: number;
}

interface HeroSectionProps {
  className?: string;
}

const DEFAULT_HERO: HeroSection = {
  id: 'default',
  title: '',
  subtitle: '',
  ctaText: '',
  ctaLink: 'https://greenpeel.mn/shop',
  type: 'VIDEO',
  mediaUrl: '/banner.mp4', // Served directly from public folder
  thumbnailUrl: '', // Optional: add a thumbnail in public/
  order: 0,
};

const HeroSection: React.FC<HeroSectionProps> = ({ className = '' }) => {
  const isMobile = useIsMobile();
  const { kind } = useParams();
  const { data, loading, error } = useQuery<{ activeHeroes: HeroSection[] }>(HEROES, {
    fetchPolicy: 'cache-first',
  });

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const controls = useAnimationControls();

  // Use database heroes if available, otherwise use default
  const heroes = useMemo(() => {
    const dbHeroes = data?.activeHeroes || [];
    return dbHeroes.length > 0 ? dbHeroes : [DEFAULT_HERO];
  }, [data?.activeHeroes]);

  // Auto-slide functionality
  useEffect(() => {
    if (!isPlaying || heroes.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroes.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isPlaying, heroes.length]);

  // Reset slide when heroes change
  useEffect(() => {
    setCurrentSlide(0);
  }, [heroes]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  const goToNextSlide = useCallback(async () => {
    if (heroes.length <= 1) return;

    await controls.start({ x: '-100%', opacity: 0, transition: { duration: 0.4 } });
    setCurrentSlide((prev) => (prev + 1) % heroes.length);
    controls.set({ x: '100%' });
    await controls.start({ x: 0, opacity: 1, transition: { duration: 0.4 } });
  }, [heroes.length, controls]);

  const goToPreviousSlide = useCallback(async () => {
    if (heroes.length <= 1) return;

    await controls.start({ x: '100%', opacity: 0, transition: { duration: 0.4 } });
    setCurrentSlide((prev) => (prev - 1 + heroes.length) % heroes.length);
    controls.set({ x: '-100%' });
    await controls.start({ x: 0, opacity: 1, transition: { duration: 0.4 } });
  }, [heroes.length, controls]);

  const togglePlayPause = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  // Touch handlers
  const handleTouchStart = (e: React.TouchEvent) => setTouchStart(e.targetTouches[0].clientX);
  const handleTouchMove = (e: React.TouchEvent) => setTouchEnd(e.targetTouches[0].clientX);
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isSwipe = Math.abs(distance) >= 50;

    if (isSwipe) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      distance > 0 ? goToNextSlide() : goToPreviousSlide();
    }
    setTouchStart(0);
    setTouchEnd(0);
  };

  // Show skeleton while loading
  if (loading) {
    return <HeroSkeleton />;
  }

  // Never return null anymore — we always have at least the default hero
  const currentHero = heroes[currentSlide];
  const hasMultiple = heroes.length > 1;

  const renderMedia = (hero: HeroSection) => {
    if (hero.type === 'VIDEO') {
      return (
        <VideoBackground
          videoUrl={hero.mediaUrl}
          thumbnailUrl={hero.thumbnailUrl}
          isPlaying={isPlaying && currentSlide === heroes.indexOf(hero)}
        />
      );
    }
    return <ImageBackground imageUrl={fileUrl(hero.mediaUrl)} />;
  };

  return (
    <section
      className={`relative w-full h-[60vh] sm:h-[70vh] md:h-[80vh] lg:h-screen overflow-hidden ${className}`}
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchMove={isMobile ? handleTouchMove : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="absolute inset-0"
        >
          {renderMedia(currentHero)}

          <div className="absolute inset-0 bg-black bg-opacity-40" />

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut', delay: 0.2 }}
            className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white drop-shadow-2xl leading-tight">
              {currentHero.title}
            </h1>

            {currentHero.subtitle && (
              <p className="mt-6 text-lg sm:text-xl md:text-2xl lg:text-3xl text-white drop-shadow-lg max-w-4xl">
                {currentHero.subtitle}
              </p>
            )}

            {currentHero.ctaText && currentHero.ctaLink && (
              <motion.a
                href={currentHero.ctaLink}
                whileHover={{ scale: 1.08 }}
                whileTap={{ scale: 0.95 }}
                className="mt-10 inline-flex items-center px-8 py-4 text-lg font-semibold text-black bg-white rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300"
              >
                {currentHero.ctaText}
                <span className="ml-3 text-xl">→</span>
              </motion.a>
            )}
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Controls only if multiple slides */}
      {hasMultiple && (
        <>
          {/* Dots */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
            {heroes.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className={`h-3 rounded-full transition-all duration-300 ${
                  i === currentSlide
                    ? 'bg-white w-10'
                    : 'bg-white/50 w-3 hover:bg-white/80'
                }`}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

          {/* Prev/Next Arrows (Desktop only) */}
          {!isMobile && (
            <>
              <button
                onClick={goToPreviousSlide}
                className="absolute left-6 top-1/2 -translate-y-1/2 z-20 p-4 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/30 transition"
                aria-label="Previous slide"
              >
                <ChevronLeft size={32} className="text-white" />
              </button>
              <button
                onClick={goToNextSlide}
                className="absolute right-6 top-1/2 -translate-y-1/2 z-20 p-4 bg-white/20 backdrop-blur-md rounded-full hover:bg-white/30 transition"
                aria-label="Next slide"
              >
                <ChevronRight size={32} className="text-white" />
              </button>
            </>
          )}
        </>
      )}
    </section>
  );
};

// VideoBackground – now supports public/ files directly
const VideoBackground: React.FC<{
  videoUrl: string;
  thumbnailUrl?: string;
  isPlaying: boolean;
}> = ({ videoUrl, thumbnailUrl, isPlaying }) => {
  const src = videoUrl.startsWith('http') ? fileUrl(videoUrl) : videoUrl; // public file if no http

  return (
    <div className="absolute inset-0 overflow-hidden">
      <video
        autoPlay
        muted
        loop
        playsInline
        poster={thumbnailUrl}
        className="w-full h-full object-cover"
        style={{ display: isPlaying ? 'block' : 'none' }}
      >
        <source src={src} type="video/mp4" />
      </video>

      {/* Fallback thumbnail when paused */}
      {!isPlaying && thumbnailUrl && (
        <img
          src={thumbnailUrl}
          alt="Video paused"
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}
    </div>
  );
};

const ImageBackground: React.FC<{ imageUrl: string }> = ({ imageUrl }) => (
  <img
    src={imageUrl}
    alt="Hero background"
    className="absolute inset-0 w-full h-full object-cover"
    loading="lazy"
  />
);

const HeroSkeleton: React.FC = () => (
  <div className="relative w-full h-screen bg-gradient-to-br from-gray-800 via-gray-900 to-black">
    <div className="absolute inset-0 bg-black/40" />
    <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-8">
      <div className="h-20 w-full max-w-4xl bg-white/10 backdrop-blur-sm rounded-2xl animate-pulse mb-8" />
      <div className="h-10 w-96 bg-white/10 rounded-xl animate-pulse" />
    </div>
  </div>
);

export default HeroSection;